<div class="flex flex-col gap-4 p-4">
  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-default text-xl font-bold">Facilities Dashboard</h1>
      <p class="text-secondary text-sm">Service requests, inspections and work order management</p>
    </div>
  </div>

  <!-- Filter Bar -->
  <div class="rounded-xl border bg-white px-4 py-1">
    <app-global-filter-panel [config]="filterConfig" (filtersChanged)="onFiltersChanged($event)" />
  </div>

  <!-- Service Requests -->
  <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <h3 class="text-secondary mb-3 text-sm font-semibold uppercase tracking-wide">Service Requests</h3>
    @if (!hasWorkOrderPermission) {
      <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
        <mat-icon class="!text-base text-red-400">lock</mat-icon>
        Permissions Restricted
      </div>
    } @else if (loadingServiceRequests) {
      <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
    } @else if (serviceRequestsData) {
      <div class="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <a routerLink="/eservices/list" [queryParams]="serviceRequestQueryParams()" class="rounded-lg border border-blue-100 bg-blue-50 p-3 text-center transition-shadow hover:shadow-md">
          <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Pending</div>
          <div class="mt-0.5 text-lg font-bold text-blue-700">{{ serviceRequestsData.pending_requests ?? 0 }}</div>
        </a>
        <a routerLink="/eservices/list" [queryParams]="serviceRequestQueryParams()" class="rounded-lg border border-red-100 bg-red-50 p-3 text-center transition-shadow hover:shadow-md">
          <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Pending &gt; 7 Days</div>
          <div class="mt-0.5 text-lg font-bold text-red-700">{{ serviceRequestsData.overdue_requests ?? 0 }}</div>
        </a>
      </div>
      @if (serviceRequestsData.by_type?.length > 0) {
        <div class="mb-2 flex items-center gap-2">
          <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">Pending by Type</span>
          <div class="flex-1 border-t border-gray-100"></div>
        </div>
        <div class="divide-y divide-gray-50">
          @for (p of serviceRequestsData.by_type; track $index) {
            <a routerLink="/eservices/list" [queryParams]="serviceRequestQueryParams(p.type)" class="flex items-center justify-between py-1.5 transition-colors hover:bg-gray-50">
              <span class="rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize leading-none" [ngClass]="getPriorityColor(p.priority)">
                {{ p.priority ?? 'Unknown' }}
              </span>
              <span class="text-default text-xs font-bold">{{ p.count }}</span>
            </a>
          }
        </div>
      }
    }
  </div>

  <!-- Row 3: Inspections + Work Orders -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <!-- Inspection Summary -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 class="text-secondary mb-3 text-sm font-semibold uppercase tracking-wide">Inspection Summary</h3>
      @if (!hasInspectionPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingInspection) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (inspectionData) {
        <div class="mb-4 grid grid-cols-3 gap-3">
          <a routerLink="/inspections/reports" [queryParams]="openInspectionsQueryParams" class="rounded-lg border border-orange-100 bg-orange-50 p-3 text-center transition-shadow hover:shadow-md">
            <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Open Inspections</div>
            <div class="mt-0.5 text-xl font-bold text-orange-700">{{ inspectionData.open_inspections ?? 0 }}</div>
          </a>
          <a routerLink="/inspections/reports" [queryParams]="completedInspectionsQueryParams" class="rounded-lg border border-green-100 bg-green-50 p-3 text-center transition-shadow hover:shadow-md">
            <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Approved Inspections</div>
            <div class="mt-0.5 text-xl font-bold text-green-700">{{ inspectionData.approved_inspections ?? 0 }}</div>
          </a>
          <a routerLink="/inspections/snags" class="rounded-lg border border-red-100 bg-red-50 p-3 text-center transition-shadow hover:shadow-md">
            <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Active Snags</div>
            <div class="mt-0.5 text-xl font-bold text-red-700">{{ inspectionData.active_snags ?? 0 }}</div>
          </a>
        </div>
        @if (inspectionData.snags_by_severity?.length > 0 || inspectionData.snags_by_category?.length > 0) {
          <div class="grid grid-cols-2 gap-4">
            <!-- Snags by Severity -->
            @if (inspectionData.snags_by_severity?.length > 0) {
              <div>
                <div class="mb-2 flex items-center gap-2">
                  <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">By Severity</span>
                  <div class="flex-1 border-t border-gray-100"></div>
                </div>
                <div>
                  @for (s of inspectionData.snags_by_severity; track $index) {
                    <a routerLink="/inspections/snags" [queryParams]="severityQueryParams(s.priority)" class="flex items-center justify-between py-1.5 transition-colors hover:bg-gray-50">
                      <span class="inline-flex items-center gap-1 text-xs font-medium capitalize text-gray-700">
                        <app-status-dot [color]="s.priority | snagRisk" [title]="s.priority"></app-status-dot>
                        {{ s.priority ?? 'Unknown' }}
                      </span>
                      <span class="text-default text-sm font-semibold">{{ s.count }}</span>
                    </a>
                  }
                </div>
              </div>
            }
            <!-- Snags by Category -->
            @if (inspectionData.snags_by_category?.length > 0) {
              <div>
                <div class="mb-2 flex items-center gap-2">
                  <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">By Category</span>
                  <div class="flex-1 border-t border-gray-100"></div>
                </div>
                <div>
                  @for (c of inspectionData.snags_by_category; track $index) {
                    <a routerLink="/inspections/snags" [queryParams]="categoryQueryParams(c.id)" class="flex items-center justify-between py-1.5 transition-colors hover:bg-gray-50">
                      <span class="min-w-0 flex-1 truncate text-xs font-medium text-gray-700">{{ c.category }}</span>
                      <span class="ml-2 shrink-0 text-sm font-semibold text-gray-800">{{ c.count }}</span>
                    </a>
                  }
                </div>
              </div>
            }
          </div>
        }
      }
    </div>

    <!-- Work Order Summary -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 class="text-secondary mb-3 text-sm font-semibold uppercase tracking-wide">Work Orders by Status</h3>
      @if (!hasWorkOrderPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingWo) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (workOrderData) {
        <div class="divide-y divide-gray-50">
          @for (s of workOrderData.by_status; track $index) {
            <a [routerLink]="getWorkOrderRoute(s.status)" [queryParams]="getWorkOrderQueryParams(s.status)" class="flex items-center justify-between py-1.5 transition-colors hover:bg-gray-50">
              <span class="inline-flex items-center gap-1 text-xs font-medium capitalize text-gray-700">
                <app-status-dot [color]="s.status | color" [title]="s.status"></app-status-dot>
                {{ s.status ?? 'Unknown' }}
              </span>
              <span class="text-default text-xs font-bold">{{ s.count }}</span>
            </a>
          }
        </div>
        @if (workOrderData.completed_this_period) {
          <div class="text-secondary mt-3 border-t pt-3 text-sm">
            <span class="font-semibold text-green-600">{{ workOrderData.completed_this_period }}</span> completed {{ getPeriodLabel() }}
          </div>
        }
      }
    </div>
  </div>

  <!-- Bottom Row: Work Queue + Timeline -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm lg:col-span-2">
      <app-work-queue-list [filters]="filters" [requiredPermission]="'workorder_view'" />
    </div>
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-activity-timeline [requiredPermission]="'activity_logs_other'" />
    </div>
  </div>
</div>
